/* =============================================================
   PEACH PLEASE — landing page enhancements
   Vanilla JS, no dependencies, no globals beyond one load guard.

   Everything here is progressive enhancement. If this file never
   runs (blocked, errored, JS disabled) the page is still complete:
     • all content is visible (reveal CSS is only armed by the class
       this script adds)
     • add-to-cart forms POST normally to routes.cart_add_url
     • category tabs stay hidden and every product is shown
     • intro chips are ordinary anchor links
     • hotspot labels are reachable on hover/focus via CSS, and the
       same products are listed as plain links beneath the image
     • the sticky mobile bar never appears

   Respects prefers-reduced-motion, avoids layout shift (opacity and
   transform only), and re-initialises for the Shopify theme editor
   via shopify:section:load.

   Selectors, classes and data attributes are all namespaced with the
   lp-peach-please prefix so nothing can collide with the theme.
   ============================================================= */
(function () {
  'use strict';

  // One <script> tag is rendered per section — run the work once.
  if (window.__lpPeachPleaseLoaded) return;
  window.__lpPeachPleaseLoaded = true;

  var ROOT = '.lp-peach-please';
  var READY_ATTR = 'data-lp-peach-please-ready';
  var CLASS_ENHANCED = 'lp-peach-please--enhanced';
  var CLASS_REVEAL_READY = 'lp-peach-please--reveal-ready';
  var CLASS_VISIBLE = 'is-visible';

  var reduceQuery = window.matchMedia
    ? window.matchMedia('(prefers-reduced-motion: reduce)')
    : null;
  var prefersReduced = !!(reduceQuery && reduceQuery.matches);
  var supportsObserver = 'IntersectionObserver' in window;

  function each(list, fn) {
    Array.prototype.forEach.call(list || [], fn);
  }

  /* ---------- Scroll reveals -------------------------------- */

  var revealObserver = null;

  function getRevealObserver() {
    if (revealObserver) return revealObserver;
    revealObserver = new IntersectionObserver(
      function (entries) {
        each(entries, function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add(CLASS_VISIBLE);
          revealObserver.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -6% 0px', threshold: 0.06 }
    );
    return revealObserver;
  }

  function armReveals(root) {
    if (prefersReduced || !supportsObserver) return;
    root.classList.add(CLASS_REVEAL_READY);

    each(root.querySelectorAll('[data-lp-peach-please-reveal]'), function (item, index) {
      // Gentle stagger, capped so later items never feel laggy.
      item.style.transitionDelay = Math.min(index * 65, 260) + 'ms';
      getRevealObserver().observe(item);
    });
  }

  /* ---------- Category tabs (filter the product grid) ------- */

  function findTab(key) {
    if (!key) return null;
    // Category names can contain "&" and spaces; only quotes and
    // backslashes would break the attribute selector.
    var safe = key.replace(/["\\]/g, '');
    return document.querySelector('[data-lp-peach-please-tab="' + safe + '"]');
  }

  function armTabs(root) {
    var tabRow = root.querySelector('[data-lp-peach-please-tabs]');
    if (!tabRow) return;

    var grid = root.querySelector('[data-lp-peach-please-grid]');
    var status = root.querySelector('[data-lp-peach-please-tab-status]');
    if (!grid) return;

    var tabs = tabRow.querySelectorAll('[data-lp-peach-please-tab]');
    var cards = grid.querySelectorAll('[data-lp-peach-please-card]');
    if (!tabs.length || !cards.length) return;

    function apply(filter) {
      var shown = 0;

      each(cards, function (card) {
        var tags = (card.getAttribute('data-lp-peach-please-tags') || '').toLowerCase();
        var match = filter === '' || tags.indexOf('|' + filter + '|') !== -1;
        card.hidden = !match;
        if (match) shown++;
      });

      each(tabs, function (tab) {
        var value = (tab.getAttribute('data-lp-peach-please-tab') || '').toLowerCase();
        tab.setAttribute('aria-pressed', value === filter ? 'true' : 'false');
      });

      if (status) {
        status.textContent =
          shown === cards.length
            ? 'Showing all ' + cards.length + ' products.'
            : 'Showing ' + shown + ' of ' + cards.length + ' products.';
      }
    }

    tabRow.addEventListener('click', function (event) {
      if (!event.target || !event.target.closest) return;
      var tab = event.target.closest('[data-lp-peach-please-tab]');
      if (!tab || !tabRow.contains(tab)) return;
      apply((tab.getAttribute('data-lp-peach-please-tab') || '').toLowerCase());
    });

    // Hide any tab whose tag matches nothing in the rendered grid, so
    // merchants never ship an empty category.
    each(tabs, function (tab) {
      var value = (tab.getAttribute('data-lp-peach-please-tab') || '').toLowerCase();
      if (value === '') return;
      var found = false;
      each(cards, function (card) {
        var tags = (card.getAttribute('data-lp-peach-please-tags') || '').toLowerCase();
        if (tags.indexOf('|' + value + '|') !== -1) found = true;
      });
      if (!found) tab.hidden = true;
    });
  }

  /* ---------- Intro chips -> pre-filter the grid ------------ */
  /* The chip is a real anchor, so the jump works without this. When a
     chip names a category, we also activate that tab before the
     browser scrolls, so the visitor lands on a filtered grid. */

  function armNav(root) {
    var nav = root.querySelector('[data-lp-peach-please-nav]');
    if (!nav) return;

    nav.addEventListener('click', function (event) {
      if (!event.target || !event.target.closest) return;
      var link = event.target.closest('[data-lp-peach-please-nav-tab]');
      if (!link) return;

      var tab = findTab((link.getAttribute('data-lp-peach-please-nav-tab') || '').toLowerCase());
      if (tab && !tab.hidden) tab.click();
    });
  }

  /* ---------- Behind-the-Chair hotspots -------------------- */

  function closeHotspots(scope, except) {
    each(scope.querySelectorAll('[data-lp-peach-please-hotspot].is-open'), function (spot) {
      if (spot === except) return;
      spot.classList.remove('is-open');
      var trigger = spot.querySelector('[data-lp-peach-please-hotspot-trigger]');
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
    });
  }

  function armHotspots(root) {
    var scene = root.querySelector('[data-lp-peach-please-scene]');
    if (!scene) return;

    scene.addEventListener('click', function (event) {
      if (!event.target || !event.target.closest) return;
      var trigger = event.target.closest('[data-lp-peach-please-hotspot-trigger]');
      if (!trigger) return;

      var spot = trigger.closest('[data-lp-peach-please-hotspot]');
      if (!spot) return;

      var willOpen = !spot.classList.contains('is-open');
      closeHotspots(scene, spot);
      spot.classList.toggle('is-open', willOpen);
      trigger.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
    });

    scene.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape' && event.key !== 'Esc') return;
      closeHotspots(scene, null);
    });

    document.addEventListener('click', function (event) {
      if (scene.contains(event.target)) return;
      closeHotspots(scene, null);
    });
  }

  /* ---------- Sticky mobile CTA ---------------------------- */

  function armSticky(root) {
    var bar = root.querySelector('[data-lp-peach-please-sticky]');
    if (!bar || !supportsObserver) return;

    var anchor = root.querySelector('[data-lp-peach-please-sticky-anchor]') || root;

    // Lift the bar out of the hero's stacking context so nothing later
    // on the page can paint over it, dropping any bar left behind by a
    // previous render of this section in the theme editor.
    each(document.querySelectorAll('body > [data-lp-peach-please-sticky]'), function (stale) {
      if (stale !== bar && stale.parentNode) stale.parentNode.removeChild(stale);
    });
    document.body.appendChild(bar);
    bar.classList.add('is-armed');

    // Show the bar once the hero has scrolled out of view.
    new IntersectionObserver(
      function (entries) {
        each(entries, function (entry) {
          bar.classList.toggle(CLASS_VISIBLE, !entry.isIntersecting && !bar.dataset.lpPeachPleaseSuppressed);
        });
      },
      { threshold: 0 }
    ).observe(anchor);

    // Get out of the way of the closing CTA so it never covers it.
    var closer = document.querySelector('[data-lp-peach-please-sticky-hide]');
    if (!closer) return;

    new IntersectionObserver(
      function (entries) {
        each(entries, function (entry) {
          if (entry.isIntersecting) {
            bar.dataset.lpPeachPleaseSuppressed = 'true';
            bar.classList.remove(CLASS_VISIBLE);
          } else {
            delete bar.dataset.lpPeachPleaseSuppressed;
          }
        });
      },
      { threshold: 0.15 }
    ).observe(closer);
  }

  /* ---------- Add to cart ---------------------------------- */
  /* Prefers this theme's own cart-drawer integration
     (window.addMultipleProducts, defined in assets/custom.js) so a
     click here opens exactly the same drawer as the rest of the site.
     Falls back to the Cart AJAX API, then to a plain form POST if
     fetch itself fails. Every button on the page — product cards, foil
     columns, tool cards and the bundle — adds a single variant. */

  function setButtonState(button, state, label) {
    if (!button) return;

    if (!button.dataset.lpPeachPleaseLabel) {
      button.dataset.lpPeachPleaseLabel = button.textContent.trim();
    }

    if (state === 'loading') {
      button.disabled = true;
      button.textContent = label || 'Adding…';
    } else if (state === 'added') {
      button.disabled = false;
      button.textContent = label || 'Added!';
      window.setTimeout(function () {
        button.textContent = button.dataset.lpPeachPleaseLabel;
      }, 1700);
    } else {
      button.disabled = false;
      button.textContent = button.dataset.lpPeachPleaseLabel;
    }
  }

  function submitAddForm(form) {
    var button = form.querySelector('[data-lp-peach-please-add-button]');
    var idField = form.querySelector('[name="id"]');
    var quantityField = form.querySelector('[name="quantity"]');
    var variantId = idField && idField.value;
    var quantity = (quantityField && quantityField.value) || '1';

    if (!variantId) return;

    setButtonState(button, 'loading', form.getAttribute('data-lp-peach-please-loading-label'));

    if (typeof window.addMultipleProducts === 'function') {
      var handled = window.addMultipleProducts([variantId], [''], [quantity], false, false);

      if (handled === false) {
        setButtonState(button, 'idle');
        return;
      }

      document.dispatchEvent(
        new CustomEvent('lp-peach-please:cart-add', {
          detail: { variantId: variantId, quantity: quantity }
        })
      );
      window.setTimeout(function () {
        setButtonState(button, 'added', form.getAttribute('data-lp-peach-please-added-label'));
      }, 600);
      return;
    }

    fetch(form.getAttribute('action') || '/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
      body: new FormData(form)
    })
      .then(function (response) {
        if (!response.ok) throw new Error('Cart add failed');
        return response.json();
      })
      .then(function (item) {
        document.dispatchEvent(new CustomEvent('lp-peach-please:cart-add', { detail: item }));
        setButtonState(button, 'added', form.getAttribute('data-lp-peach-please-added-label'));
      })
      .catch(function () {
        setButtonState(button, 'idle');
        form.submit();
      });
  }

  document.addEventListener('submit', function (event) {
    var form = event.target.closest && event.target.closest('[data-lp-peach-please-add-form]');
    if (!form) return;
    event.preventDefault();
    submitAddForm(form);
  });

  /* ---------- Boot ---------------------------------------- */

  function prepareRoot(root) {
    if (root.getAttribute(READY_ATTR) === 'true') return;
    root.setAttribute(READY_ATTR, 'true');
    root.classList.add(CLASS_ENHANCED);

    armReveals(root);
    armTabs(root);
    armNav(root);
    armHotspots(root);
    armSticky(root);
  }

  function prepare(scope) {
    if (!scope) return;

    if (scope.matches && scope.matches(ROOT)) {
      prepareRoot(scope);
      return;
    }

    if (!scope.querySelectorAll) return;
    each(scope.querySelectorAll(ROOT), prepareRoot);
  }

  function init() {
    prepare(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-arm sections added or re-rendered inside the theme editor.
  document.addEventListener('shopify:section:load', function (event) {
    prepare(event.target);
  });
})();
